import { Save, ShieldCheck } from "lucide-react";
import { useEffect, useState } from "react";
import { api } from "../api";
import { LoadingState, PageHeader, SectionHeading } from "../components";
import { useAppData } from "../context";
import type { Settings as SettingsType } from "../types";

export function Settings() {
  const { data, refresh } = useAppData();
  const [form, setForm] = useState<SettingsType | null>(null);
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (data) setForm(data.settings); }, [data]);
  if (!data || !form) return <LoadingState />;
  const editable = true;
  const fields: Array<{ key: keyof SettingsType; label: string; suffix: string; min: number; max: number }> = [
    { key: "approvalItemThreshold", label: "商品数量审批阈值", suffix: "条", min: 1, max: 10000 },
    { key: "approvalPriceChangePercent", label: "收益变化审批阈值", suffix: "%", min: 1, max: 100 },
    { key: "accountConcurrency", label: "单账号并发", suffix: "请求", min: 1, max: 20 },
    { key: "globalConcurrency", label: "全局并发", suffix: "请求", min: 1, max: 100 },
    { key: "verifyDelaySeconds", label: "首次回读延迟", suffix: "秒", min: 1, max: 60 },
    { key: "retryLimit", label: "提交重试上限", suffix: "次", min: 0, max: 10 },
    { key: "batchSize", label: "自动拆批大小", suffix: "条", min: 1, max: 1000 },
    { key: "batchFailureThreshold", label: "单批失败暂停阈值", suffix: "条", min: 1, max: 1000 },
    { key: "maxRows", label: "单文件最大行数", suffix: "行", min: 100, max: 100000 },
    { key: "maxFileMb", label: "单文件最大体积", suffix: "MB", min: 1, max: 100 },
  ];
  async function save() {
    if (!form) return;
    setBusy(true); setMessage("");
    try { await api.saveSettings(form); await refresh(); setMessage("系统参数已保存并写入审计。" ); }
    catch (error) { setMessage(error instanceof Error ? error.message : "保存失败。" ); }
    finally { setBusy(false); }
  }
  return <>
    <PageHeader eyebrow="POLICY / SYSTEM" title="规则与执行参数" actions={<button className="button primary" disabled={busy} title="保存设置" onClick={() => void save()}><Save size={17} />保存设置</button>} />
    {message ? <div className="inline-message" role="status">{message}</div> : null}
    <div className="settings-grid">
      <section className="workspace-band"><SectionHeading title="审批、拆批与限流" meta="修改后仅影响新任务" /><div className="settings-fields">{fields.slice(0, 8).map((field) => <NumberField key={field.key} field={field} value={Number(form[field.key])} disabled={!editable} onChange={(value) => setForm({ ...form, [field.key]: value })} />)}</div></section>
      <section className="workspace-band"><SectionHeading title="文件与规则" meta={`规则版本 ${form.ruleVersion}`} /><div className="settings-fields">{fields.slice(8).map((field) => <NumberField key={field.key} field={field} value={Number(form[field.key])} disabled={!editable} onChange={(value) => setForm({ ...form, [field.key]: value })} />)}</div><div className="policy-lock"><ShieldCheck size={18} /><div><strong>能力矩阵门槛</strong><span>生产模式只允许已授权账号、归属校验通过的商品进入执行队列。</span></div></div></section>
    </div>
  </>;
}

function NumberField({ field, value, disabled, onChange }: { field: { label: string; suffix: string; min: number; max: number }; value: number; disabled: boolean; onChange: (value: number) => void }) {
  return <label className="number-field"><span>{field.label}</span><div><input type="number" value={value} min={field.min} max={field.max} disabled={disabled} onChange={(event) => onChange(Number(event.target.value))} /><strong>{field.suffix}</strong></div></label>;
}
